import { useEffect, useState, useRef } from "react";

// ── Instagram QR scanner card ─────────────────────────────────
const INSTAGRAM_QR_IMAGE = "https://pub-fecbbbe474e741bbb4c68ff66eb61385.r2.dev/instagram/instagram-qr.png";
const INSTAGRAM_HANDLE   = "@sagar_nyc";
const INSTAGRAM_URL      = "https://www.instagram.com/sagar_nyc";

// ── Gallery photos ────────────────────────────────────────────
// Replace src values with your real store/product photo paths.
// Put images in /public/images/gallery/ and match the filenames below.
const GALLERY_PHOTOS = [
  { src: "/images/gallery/store-1.jpg",    caption: "Our Showroom", tag: "Store"      },
  { src: "/images/gallery/saree-1.jpg",    caption: "Saree",     tag: "Collection" },
  { src: "/images/gallery/store-2.jpg",    caption: "Bridal Section",               tag: "Store"      },
  { src: "/images/gallery/lehenga-1.jpg",  caption: "Saree",     tag: "Collection" },
  { src: "/images/gallery/store-3.jpg",    caption: "Store ",           tag: "Store"      },
  { src: "/images/gallery/customer-1.jpg", caption: "Real Customer Look",           tag: "Customer"   },
  { src: "/images/gallery/dupatta-1.jpg",  caption: "Lehangha",        tag: "Collection" },
  { src: "/images/gallery/store-4.jpg",    caption: "Festival Ready Picks",         tag: "Store"      },
];

const PLACEHOLDER_GRADIENTS = [
  "from-rose-200 to-pink-300",
  "from-amber-200 to-orange-300",
  "from-violet-200 to-purple-300",
  "from-emerald-200 to-teal-300",
  "from-sky-200 to-blue-300",
  "from-fuchsia-200 to-pink-300",
  "from-lime-200 to-green-300",
  "from-red-200 to-rose-300",
];

const TAG_COLORS = {
  Store:      "bg-stone-900/70 text-white",
  Collection: "bg-rose-500/80  text-white",
  Customer:   "bg-emerald-500/80 text-white",
};

// ── Auto-cycling Gallery component ───────────────────────────
function Gallery() {
  const [activeIdx, setActiveIdx] = useState(0);
  const [paused,    setPaused]    = useState(false);
  const [imgErrors, setImgErrors] = useState({});
  const intervalRef               = useRef(null);
  const total                     = GALLERY_PHOTOS.length;

  useEffect(() => {
    if (paused) return;
    intervalRef.current = setInterval(() => {
      setActiveIdx(prev => (prev + 1) % total);
    }, 2000);
    return () => clearInterval(intervalRef.current);
  }, [paused, total]);

  function goTo(idx) {
    setActiveIdx(idx);
    setPaused(true);
    clearInterval(intervalRef.current);
    setTimeout(() => setPaused(false), 4000);
  }

  const prev = () => goTo((activeIdx - 1 + total) % total);
  const next = () => goTo((activeIdx + 1) % total);

  return (
    <div className="mt-20">
      {/* Section header */}
      <div className="text-center mb-10">
        <span className="text-xs font-semibold tracking-[0.2em] uppercase text-rose-400 block mb-2">
          Inside Our World
        </span>
        <h2 className="text-3xl md:text-4xl font-bold text-stone-900 serif">
          Store & Collection Gallery
        </h2>
        <p className="text-stone-400 text-sm mt-3 max-w-md mx-auto">
          A peek inside our showroom and the pieces we love.
        </p>
      </div>

      {/* ── Main featured card ── */}
      <div
        className="relative mx-auto rounded-3xl overflow-hidden shadow-2xl border border-stone-100 bg-white"
        style={{ maxWidth: 720, aspectRatio: "16/10" }}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {GALLERY_PHOTOS.map((photo, idx) => (
          <div
            key={idx}
            className={`absolute inset-0 transition-opacity duration-700 ${idx === activeIdx ? "opacity-100 z-10" : "opacity-0 z-0"}`}
          >
            {imgErrors[idx] ? (
              <div className={`w-full h-full bg-gradient-to-br ${PLACEHOLDER_GRADIENTS[idx % PLACEHOLDER_GRADIENTS.length]} flex flex-col items-center justify-center gap-3`}>
                <svg className="w-12 h-12 text-white/60" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3 20.25h18M3.75 3h16.5A.75.75 0 0121 3.75v13.5a.75.75 0 01-.75.75H3.75A.75.75 0 013 17.25V3.75A.75.75 0 013.75 3z" />
                </svg>
                <p className="text-white font-semibold text-sm">{photo.caption}</p>
                <p className="text-white/60 text-xs">Add photo → {photo.src}</p>
              </div>
            ) : (
              <img
                src={photo.src}
                alt={photo.caption}
                className="w-full h-full object-cover"
                onError={() => setImgErrors(prev => ({ ...prev, [idx]: true }))}
              />
            )}

            {/* Overlay gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />

            {/* Caption */}
            <div className="absolute bottom-0 left-0 right-0 px-5 pb-5 flex items-end justify-between">
              <div>
                <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full backdrop-blur-sm mb-2 inline-block ${TAG_COLORS[photo.tag] || "bg-black/50 text-white"}`}>
                  {photo.tag}
                </span>
                <p className="text-white font-bold text-lg leading-tight drop-shadow">{photo.caption}</p>
              </div>
              <span className="text-white/50 text-xs font-mono">{activeIdx + 1}/{total}</span>
            </div>
          </div>
        ))}

        {/* Arrows */}
        <button onClick={prev} aria-label="Previous"
          className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full bg-black/30 backdrop-blur-sm text-white flex items-center justify-center hover:bg-black/50 transition">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
        </button>
        <button onClick={next} aria-label="Next"
          className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full bg-black/30 backdrop-blur-sm text-white flex items-center justify-center hover:bg-black/50 transition">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
        </button>

        {/* Paused pill */}
        {paused && (
          <div className="absolute top-3 right-3 z-20 bg-black/40 backdrop-blur-sm text-white text-[10px] font-semibold px-2.5 py-1 rounded-full">
            Paused
          </div>
        )}
      </div>

      {/* ── Thumbnail strip ── */}
      <div className="mt-4 flex gap-2 justify-center flex-wrap px-2">
        {GALLERY_PHOTOS.map((photo, idx) => (
          <button
            key={idx}
            onClick={() => goTo(idx)}
            aria-label={`Photo ${idx + 1}`}
            className={`relative w-12 h-12 md:w-14 md:h-14 rounded-xl overflow-hidden border-2 transition-all duration-300 flex-shrink-0 ${
              idx === activeIdx
                ? "border-rose-400 scale-110 shadow-md shadow-rose-200"
                : "border-transparent opacity-55 hover:opacity-80"
            }`}
          >
            {imgErrors[idx] ? (
              <div className={`w-full h-full bg-gradient-to-br ${PLACEHOLDER_GRADIENTS[idx % PLACEHOLDER_GRADIENTS.length]}`} />
            ) : (
              <img src={photo.src} alt={photo.caption} className="w-full h-full object-cover"
                onError={() => setImgErrors(prev => ({ ...prev, [idx]: true }))} />
            )}
          </button>
        ))}
      </div>

      {/* ── Dot progress bar ── */}
      <div className="mt-4 flex gap-1.5 justify-center">
        {GALLERY_PHOTOS.map((_, idx) => (
          <button
            key={idx}
            onClick={() => goTo(idx)}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              idx === activeIdx ? "bg-rose-400 w-6" : "bg-stone-200 w-2.5 hover:bg-stone-300"
            }`}
          />
        ))}
      </div>

      
    </div>
  );
}

// ── Tutorials data ────────────────────────────────────────────
const tutorials = [
  {
    platform: "youtube",
    title: "How to Drape a Saree Perfectly — Step by Step",
    embed: (
      <div className="relative w-full aspect-video bg-stone-100 overflow-hidden">
        <iframe className="w-full h-full" src="https://www.youtube.com/embed/1KFFT6bZ6Jw"
          title="How to Drape a Saree Perfectly" frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen />
      </div>
    ),
  },
  {
    platform: "instagram",
    title: "Follow Us on Instagram for Daily Style Inspiration",
    embed: (
      <div className="relative w-full bg-gradient-to-br from-fuchsia-500 via-pink-500 to-orange-400 flex flex-col items-center justify-center py-8 px-6 gap-5">
        <div className="bg-white rounded-2xl p-3 shadow-lg w-52 h-52 flex items-center justify-center overflow-hidden">
          <img src={INSTAGRAM_QR_IMAGE} alt={`QR for ${INSTAGRAM_HANDLE}`} className="w-full h-full object-contain"
            onError={e => { e.currentTarget.style.display="none"; e.currentTarget.nextSibling.style.display="flex"; }} />
          <div className="hidden w-full h-full flex-col items-center justify-center gap-2" style={{display:"none"}}>
            <p className="text-pink-700 text-xs font-bold text-center">Add QR image to<br/>/public/images/instagram-qr.png</p>
          </div>
        </div>
        <div className="text-center">
          <p className="text-white font-black text-xl tracking-tight">{INSTAGRAM_HANDLE}</p>
          <p className="text-white/80 text-xs mt-1">Scan to follow · Daily style drops</p>
        </div>
        <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-2 bg-white text-pink-600 font-bold text-sm px-5 py-2.5 rounded-full shadow hover:bg-pink-50 transition-colors">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
          </svg>
          Follow on Instagram
        </a>
      </div>
    ),
  },
  {
    platform: "youtube",
    title: "8 Dupatta Draping Styles for Lehenga — 2024 Trends",
    embed: (
      <div className="relative w-full aspect-video bg-stone-100 overflow-hidden">
        <iframe className="w-full h-full" src="https://www.youtube.com/embed/31kD20lNKCA"
          title="8 Dupatta Draping Styles for Lehenga 2024" frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen />
      </div>
    ),
  },
];

const PLATFORM_BADGE = {
  youtube: {
    label: "YouTube", color: "bg-red-100 text-red-600",
    icon: <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>,
  },
  instagram: {
    label: "Instagram", color: "bg-pink-100 text-pink-600",
    icon: <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>,
  },
};

// ── Main Page ─────────────────────────────────────────────────
export default function TutorialsPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="pt-20 min-h-screen bg-stone-50">

      {/* Hero */}
      <div className="relative overflow-hidden bg-stone-900 py-20 px-6">
        <div className="absolute inset-0 opacity-20"
          style={{ backgroundImage:"url('https://images.unsplash.com/photo-1558769132-cb1aea458c5e?w=1600&q=55')", backgroundSize:"cover", backgroundPosition:"center" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-stone-950/80 to-stone-900/50" />
        <div className="relative max-w-4xl mx-auto text-center">
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-rose-400 mb-3 block">Learn & Style</span>
          <h1 className="serif text-5xl md:text-6xl font-bold text-white mb-4">Style Tutorials</h1>
          <p className="text-white/60 text-lg font-light max-w-xl mx-auto">
            Learn how to drape, style, and wear our beautiful ethnic garments with these step-by-step guides.
          </p>
        </div>
      </div>

      {/* Tutorials grid + Gallery */}
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tutorials.map((tutorial, index) => {
            const badge = PLATFORM_BADGE[tutorial.platform];
            return (
              <div key={index} className="bg-white rounded-3xl overflow-hidden shadow-sm border border-stone-100 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 flex flex-col">
                <div className="overflow-hidden">{tutorial.embed}</div>
                <div className="p-5 flex flex-col flex-1">
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`flex items-center gap-1.5 text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wide ${badge.color}`}>
                      {badge.icon}{badge.label}
                    </span>
                  </div>
                  <h3 className="serif text-base font-semibold text-stone-900 leading-snug">{tutorial.title}</h3>
                </div>
              </div>
            );
          })}
        </div>

        {/* Gallery */}
        <Gallery />

        {/* CTA */}
        <div className="mt-16 bg-white rounded-3xl border border-stone-100 p-10 text-center shadow-sm">
          <div className="w-12 h-12 rounded-full bg-rose-50 flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-rose-500" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.069A1 1 0 0121 8.87v6.26a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z"/>
            </svg>
          </div>
          <p className="serif text-xl font-bold text-stone-900 mb-2">Want to add your own tutorials?</p>
          <p className="text-stone-400 text-sm max-w-lg mx-auto">
            This page can be connected to a CMS to let you add YouTube videos, Instagram posts, and other content without writing any code.
          </p>
        </div>
      </div>
    </div>
  );
}